import { ShoppingBag, ChevronDown, Folder, Calendar, MapPin, Trophy, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { phoneMockups, heroMosaic } from '../mock';

/* ============================================================
   MOCKUP 1 · Photo grid with "Vendida" badges
============================================================ */
export function PhotoGridMockup() {
  const { t } = useTranslation();
  const photos = [
    { src: phoneMockups.album1, price: '3.850' },
    { src: phoneMockups.album2, price: '3.850' },
    { src: phoneMockups.album3, price: '4.550' },
    { src: phoneMockups.album4, price: '4.200' },
    { src: phoneMockups.album5, price: '3.850' },
    { src: phoneMockups.album6, price: '4.200' },
    { src: phoneMockups.personA, price: '4.550' },
    { src: phoneMockups.personB, price: '4.200' },
    { src: heroMosaic.left[0].src, price: '3.850' },
    { src: heroMosaic.left[3].src, price: '4.550' },
    { src: heroMosaic.right[1].src, price: '3.360' },
    { src: heroMosaic.right[2].src, price: '4.550' },
  ];
  return (
    <div className="rounded-2xl bg-[#0d0d13] border border-white/10 p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded bg-gradient-to-br from-violet-400 to-fuchsia-500 flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-sm" />
          </div>
          <div className="font-semibold text-sm text-white">{t('mockups.dashboard.myPhotos')}</div>
          <div className="text-xs text-white/40">(5.000)</div>
        </div>
        <div className="hidden md:block text-[10px] text-white/40">{t('mockups.dashboard.onlyYours')}</div>
      </div>

      {/* Green summary banner */}
      <div className="mb-4 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-300 shrink-0">
            <ShoppingBag className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300">{t('mockups.dashboard.salesEyebrow')}</div>
            <div className="text-sm md:text-base font-semibold text-white">{t('mockups.dashboard.sold132')}</div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-emerald-300">{t('mockups.dashboard.earned')}</div>
          <div className="text-sm md:text-lg font-display font-bold text-emerald-300">$ 520.940 ARS</div>
        </div>
      </div>

      {/* Photo grid */}
      <div className="grid grid-cols-4 md:grid-cols-6 gap-1.5 md:gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-white/8">
            <img src={p.src} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full bg-emerald-500/95 text-[8px] font-semibold text-white flex items-center gap-0.5">
              <span>✓</span> {t('mockups.dashboard.sold')}
            </div>
            <div className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded-md bg-black/70 backdrop-blur-sm text-[9px] font-semibold text-white">
              +${p.price}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP 2 · Events cards ("Eventos oficiales")
============================================================ */
export function EventsMockup() {
  const { t } = useTranslation();
  const catFest = t('mockups.dashboard.catFestivals');
  const catSport = t('mockups.dashboard.catSports');
  const events = [
    { name: 'LALI - 07/06/26', date: '8 de jun de 2026', loc: 'Buenos Aires, AR', cat: catFest, fotos: '2.674', ventas: '70', gradient: 'from-violet-400 to-fuchsia-500' },
    { name: 'Airbag 30/05/26', date: '31 de may de 2026', loc: 'Buenos Aires, AR', cat: catFest, fotos: '3.351', ventas: '138', gradient: 'from-pink-400 to-rose-500' },
    { name: 'Boca vs Universidad', date: '29 de may de 2026', loc: 'CABA, AR', cat: catSport, fotos: '1.835', ventas: '58', gradient: 'from-cyan-400 to-blue-500' },
    { name: 'River vs Blooming', date: '27 de may de 2026', loc: 'CABA, AR', cat: catSport, fotos: '3.032', ventas: '75', gradient: 'from-emerald-400 to-teal-500' },
  ];
  return (
    <div className="rounded-2xl bg-[#0d0d13] border border-white/10 p-4 md:p-5">
      <div className="flex items-center gap-4 mb-4 border-b border-white/8 pb-3">
        <div className="text-[11px] text-white/50">{t('mockups.dashboard.myAlbums')}</div>
        <div className="text-[11px] text-white font-semibold border-b-2 border-fuchsia-500 pb-2 -mb-2.5">{t('mockups.dashboard.officialEvents')}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {events.map((e) => (
          <div key={e.name} className="rounded-xl bg-[#111116] border border-white/8 p-3 hover:border-violet-400/30 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${e.gradient} flex items-center justify-center shrink-0`}>
                <Folder className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="px-1.5 py-0.5 rounded bg-cyan-500/15 border border-cyan-400/25 text-[8px] font-semibold text-cyan-300">{t('mockups.dashboard.official')}</span>
                <span className="text-[9px] text-white/40">{e.cat}</span>
              </div>
            </div>
            <div className="font-semibold text-sm text-white leading-tight mb-1.5">{e.name}</div>
            <div className="flex items-center gap-2 text-[10px] text-white/50 mb-2.5">
              <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{e.date}</span>
              <span className="flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{e.loc}</span>
            </div>
            <div className="flex items-end gap-6 pt-2 border-t border-white/5">
              <div>
                <div className="font-display text-lg font-bold text-white leading-none">{e.fotos}</div>
                <div className="text-[9px] text-white/40 mt-0.5">{t('mockups.dashboard.myPhotosCol')}</div>
              </div>
              <div>
                <div className="font-display text-lg font-bold text-cyan-300 leading-none">{e.ventas}</div>
                <div className="text-[9px] text-white/40 mt-0.5">{t('mockups.dashboard.salesCol')}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP 3 · Liquidaciones (payouts dashboard)
============================================================ */
export function LiquidacionesMockup() {
  const { t } = useTranslation();
  const stats = [
    { label: t('mockups.dashboard.totalSold'), value: '$2.428.090', tone: 'emerald' },
    { label: t('mockups.dashboard.pending'), value: '$80.010', tone: 'amber' },
    { label: t('mockups.dashboard.withdrawn'), value: '$2.104.830', tone: 'blue' },
    { label: t('mockups.dashboard.pendingWithdrawals'), value: '$0', tone: 'violet' },
  ];
  const reqs = [
    { label: t('mockups.dashboard.kyc'), ok: true },
    { label: t('mockups.dashboard.bankData'), ok: true },
    { label: t('mockups.dashboard.missingInvoice'), ok: false },
  ];
  return (
    <div className="rounded-2xl bg-[#0d0d13] border border-white/10 p-4 md:p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold text-sm text-white">{t('mockups.dashboard.settlements')}</div>
          <div className="text-[10px] text-white/40 mt-0.5">{t('mockups.dashboard.settlementsSub')}</div>
        </div>
        <div className="text-[10px] text-white/40 px-2.5 py-1 rounded-full border border-white/10">{t('mockups.dashboard.waitDays')}</div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {stats.map((s) => {
          const toneMap = {
            emerald: 'from-emerald-500/15 border-emerald-400/25 text-emerald-300',
            amber: 'from-amber-500/15 border-amber-400/25 text-amber-300',
            blue: 'from-blue-500/15 border-blue-400/25 text-blue-300',
            violet: 'from-violet-500/15 border-violet-400/25 text-violet-300',
          }[s.tone];
          return (
            <div key={s.label} className={`rounded-lg border p-3 bg-gradient-to-br ${toneMap} to-transparent`}>
              <div className={`text-[9px] uppercase tracking-wider ${toneMap.split(' ').pop()}`}>{s.label}</div>
              <div className="text-sm md:text-base font-display font-bold text-white mt-1">{s.value}</div>
            </div>
          );
        })}
      </div>

      {/* Requirements */}
      <div className="rounded-lg border border-white/8 p-3 mb-3">
        <div className="text-[10px] text-white/50 mb-2">{t('mockups.dashboard.requirements')}</div>
        <div className="grid grid-cols-3 gap-1.5">
          {reqs.map((r) => (
            <div key={r.label} className={`px-2 py-1.5 rounded-md text-[10px] font-medium flex items-center gap-1 ${r.ok ? 'bg-emerald-500/10 border border-emerald-400/25 text-emerald-300' : 'bg-amber-500/10 border border-amber-400/25 text-amber-300'}`}>
              <span>{r.ok ? '✓' : '⚠'}</span> {r.label}
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming payouts */}
      <div className="text-[10px] text-white/50 mb-2">{t('mockups.dashboard.upcomingPayouts')}</div>
      <div className="grid grid-cols-3 gap-2">
        {[
          { d: '09/07', v: '$243.250', hi: true },
          { d: '18/07', v: '$48.510', hi: false },
          { d: '27/07', v: '$31.500', hi: false },
        ].map((p) => (
          <div key={p.d} className={`rounded-lg p-2.5 border ${p.hi ? 'bg-emerald-500/8 border-emerald-400/25' : 'bg-white/[0.02] border-white/8'}`}>
            <div className={`text-[9px] font-semibold ${p.hi ? 'text-emerald-300' : 'text-white/50'}`}>{p.hi && `${t('mockups.dashboard.next')} · `}{p.d}</div>
            <div className="text-sm font-display font-bold text-white mt-1">{p.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   MOCKUP 4 · Market Intel (rankings table)
============================================================ */
export function MarketIntelMockup() {
  const { t } = useTranslation();
  const rows = [
    { evento: 'Airbag 30/05/2026', vendidas: 518, subidas: '15.171', conv: '3.41%', foto: 6 },
    { evento: 'River vs San Lorenzo', vendidas: 412, subidas: '14.570', conv: '2.83%', foto: 8 },
    { evento: 'River vs Gimnasia', vendidas: 357, subidas: '12.165', conv: '2.93%', foto: 6 },
    { evento: 'River vs Rosario Central', vendidas: 345, subidas: '14.261', conv: '2.42%', foto: 5 },
    { evento: 'River vs Bragantino', vendidas: 331, subidas: '17.744', conv: '1.87%', foto: 7 },
    { evento: 'BUD SESSIONS #1 SASHA', vendidas: 300, subidas: '7.285', conv: '4.12%', foto: 5 },
    { evento: 'Airbag - Sabado', vendidas: 273, subidas: '6.381', conv: '4.28%', foto: 5 },
  ];
  return (
    <div className="rounded-2xl bg-[#0d0d13] border border-white/10 p-4 md:p-5">
      <div className="flex items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-violet-300" />
        <div className="font-display text-lg font-bold bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{t('mockups.dashboard.marketIntel')}</div>
      </div>
      <div className="text-[10px] text-white/40 mb-4">{t('mockups.dashboard.marketSub')}</div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 flex items-center justify-between px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[10px] text-white/70">
          {t('mockups.dashboard.may2026')} <ChevronDown className="w-3 h-3" />
        </div>
        <div className="flex-1 flex items-center justify-between px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/10 text-[10px] text-white/70">
          {t('mockups.dashboard.allCountries')} <ChevronDown className="w-3 h-3" />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-white/8 overflow-hidden">
        <div className="grid grid-cols-[1.5fr_.6fr_.7fr_.6fr_.5fr] gap-2 px-3 py-2 bg-white/[0.03] text-[9px] uppercase tracking-wider text-white/50 font-semibold">
          <div>{t('mockups.dashboard.colEvent')}</div>
          <div className="text-right">{t('mockups.dashboard.colSales')}</div>
          <div className="text-right">{t('mockups.dashboard.colUploads')}</div>
          <div className="text-right">{t('mockups.dashboard.colConv')}</div>
          <div className="text-right">{t('mockups.dashboard.colPhot')}</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} className={`grid grid-cols-[1.5fr_.6fr_.7fr_.6fr_.5fr] gap-2 px-3 py-2 text-[11px] items-center ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`}>
            <div className="text-white/85 truncate">{r.evento}</div>
            <div className="text-right font-semibold text-emerald-300">{r.vendidas}</div>
            <div className="text-right text-white/60">{r.subidas}</div>
            <div className="text-right text-white/60">{r.conv}</div>
            <div className="text-right text-white/60">{r.foto}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-violet-300">
        <Sparkles className="w-3 h-3" />
        {t('mockups.dashboard.privacyNote')}
      </div>
    </div>
  );
}
